import React from "react";
import { useTranslation } from "react-i18next";
import { LimitsStatus, LimitsStatusStructure } from "src/api";
import { formatNumberToLocale } from "src/utils";
import "./payment-limits.scss";

interface Props {
  limitsStatus: LimitsStatus | null;
  /** Tightest remaining headroom; used for submit enforcement upstream. */
  availableLimit: number;
  /** Wallet/notion currency the values are denominated in (e.g. USDT). */
  currency: string;
  isLoading?: boolean;
}

const PERIODS: { key: "daily" | "weekly" | "monthly"; labelKey: string }[] = [
  { key: "daily", labelKey: "PAYMENT_LIMITS.DAILY" },
  { key: "weekly", labelKey: "PAYMENT_LIMITS.WEEKLY" },
  { key: "monthly", labelKey: "PAYMENT_LIMITS.MONTHLY" },
];

/**
 * Always-expanded limit summary: a static "Available limits" title with the
 * per-period used/limit rows below it. All values are in the wallet currency.
 * A period is shown only when a cap is configured; the block hides entirely
 * when nothing is set.
 */
const PaymentLimits: React.FC<Props> = ({ limitsStatus, currency, isLoading = false }) => {
  const { t } = useTranslation();
  const payments = limitsStatus?.payments;

  const rows = PERIODS.map(({ key, labelKey }) => {
    const bucket: LimitsStatusStructure | undefined = payments?.[key];
    if (!bucket || bucket.limit == null) return null;
    const used = bucket.filled ?? 0;
    return (
      <div className="payment-limits__row" key={key}>
        <span className="payment-limits__row-label">{t(labelKey)}</span>
        <span className="payment-limits__row-value">
          {`${formatNumberToLocale(used, 2)} / ${formatNumberToLocale(bucket.limit, 2)} ${currency}`}
        </span>
      </div>
    );
  }).filter(Boolean);

  if (!isLoading && rows.length === 0) return null;

  return (
    <div className="payment-limits">
      <span className="payment-limits__title">{t("PAYMENT_LIMITS.AVAILABLE")}</span>
      <div className="payment-limits__body">{rows}</div>
    </div>
  );
};

export default PaymentLimits;
